import { Button, Form, Input, Select, Upload } from "antd";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextArea from "antd/lib/input/TextArea";
import { getAWSImage } from "../../redux/rtk/features/user/userSlice";
import { createGame, getAllGames, updateGame } from "../../redux/rtk/features/game/gameSlice";
import ImageCropper from '../ImageCropper/ImageCropper';
import { UploadOutlined } from '@ant-design/icons';

const getCroppedImg = (imageSrc, crop) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;

    if (imageSrc.startsWith('blob:')) {
      image.crossOrigin = null;
    }

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );

      try {
        canvas.toBlob((blob) => {
          if (!blob) {
            console.error('Blob conversion failed');
            reject(new Error('Canvas to Blob conversion failed'));
            return;
          }
          const croppedUrl = URL.createObjectURL(blob);
          resolve(croppedUrl);
        }, 'image/jpeg', 0.95);
      } catch (error) {
        console.error('Error in blob conversion:', error);
        reject(error);
      }
    };

    image.onerror = (error) => {
      console.error('Image loading error:', error);
      reject(error);
    };
  });
};

export default function CreateGameForm({ onClose, edit }) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { isSubmitting } = useSelector(state => state.game)
  
  // Image states
  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [sliderImage, setSliderImage] = useState(null);
  const [sliderImageUrl, setSliderImageUrl] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [originalSliderImageUrl, setOriginalSliderImageUrl] = useState('');
  
  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [currentImageType, setCurrentImageType] = useState(null); // 'game' or 'slider'

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const cropImage = async () => {
    try {
      const currentImageUrl = currentImageType === 'game' ? imageUrl : sliderImageUrl;
      const croppedImage = await getCroppedImg(currentImageUrl, croppedAreaPixels);

      const response = await fetch(croppedImage);
      const blob = await response.blob();
      const file = new File([blob], 'cropped_image.jpg', { type: 'image/jpeg' });

      if (currentImageType === 'game') {
        setImage(file);
        setImageUrl(croppedImage);
      } else {
        setSliderImage(file);
        setSliderImageUrl(croppedImage);
      }
      
      setShowCropper(false);
    } catch (e) {
      console.error('Error in cropImage:', e);
      console.error('Error details:', {
        message: e.message,
        stack: e.stack
      });
    }
  };

  const onFinish = async (values) => {
    try {
      const formData = new FormData();
      formData.append('gameName', values.gameName);
      formData.append('gameSliderHeader', values.gameSliderHeader);
      formData.append('gameRoomDescription', values.gameRoomDescription);
      formData.append('gameSuccessRate', values.gameSuccessRate || "");
      formData.append('gameDificultyLevel', values.gameDificultyLevel);
      formData.append('gamePlayTime', values.gamePlayTime);
      formData.append('gameResetTime', values.gameResetTime);
      formData.append('gameImage', image || values.gameImage);
      formData.append('gameSliderImage', sliderImage || values.gameSliderImage);

      const resp = await dispatch(
        edit?.id
          ? updateGame({ id: edit?.id, values: formData })
          : createGame(formData)
      ).unwrap();
      onClose();
      form.resetFields();
      setSliderImage("");
      setImageUrl("");
      setSliderImageUrl("");
      dispatch(getAllGames());
    } catch (error) {
      console.log("error :: ", error)
    }
  }

  const onCancel = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (edit?.id) {
      const getGameImg = async () => {
        const gameImg = await dispatch(getAWSImage(edit.values.gameImage)).unwrap();
        const sliderImg = await dispatch(getAWSImage(edit.values.gameSliderImage)).unwrap();
        setImageUrl(gameImg.url)
        setSliderImageUrl(sliderImg.url)
        setOriginalImageUrl(gameImg.url)
        setOriginalSliderImageUrl(sliderImg.url)
      }
      getGameImg()
    }
  }, [])

  return (
    <div className='flex justify-center px-6 py-4'>
      <Form
        className='w-full'
        onFinish={onFinish}
        colon={false}
        layout='vertical'
        initialValues={edit?.id ? { ...edit?.values } : {}}
        form={form}
      >
        <Form.Item
          label='Game Room Name'
          name='gameName'
          tooltip='This is a required field'
          rules={[
            { required: true, message: "Game Name is Required." },
            { whitespace: true, message: "Game Name cannot be empty" }
          ]}
        >
          <Input placeholder='Game Name' />
        </Form.Item>
        <Form.Item
          label='Game Slider Header (Short Description)'
          name='gameSliderHeader'
          tooltip='This is a required field'
          rules={[
            { required: true, message: "Game Slider Header is Required." },
            { whitespace: true, message: "Game Slider Header cannot be empty" }
          ]}
        >
          <Input placeholder='Game Slider Header' />
        </Form.Item>
        <Form.Item
          label='Game Room Detail (Long Description)'
          name='gameRoomDescription'
          tooltip='This is a required field'
          rules={[
            { required: true, message: "Game Room Detail is Required." },
            { whitespace: true, message: "Game Room Detail cannot be empty" }
          ]}
        >
          <TextArea placeholder='Game Room Detail' />
        </Form.Item>
        {/* Not Mandetory Fields */}
        <Form.Item
          label='Success Rate (%)'
          name='gameSuccessRate'
          rules={[
            {
              pattern: /^[0-9]*$/,
              message: 'Please enter numbers only'
            }
          ]}
        >
          <Input placeholder='Success Rate' />
        </Form.Item>
        <Form.Item
          label='Difficulty Level'
          name='gameDificultyLevel'
          rules={[
            { required: true, message: "Difficulty Level is Required." }
          ]}
        >
          <Select
            placeholder="Select Difficulty Level"
            options={[
              {
                value: 'Advanced',
                label: 'Advanced',
              },
              {
                value: 'Intermediate',
                label: 'Intermediate',
              },
              {
                value: 'Easy',
                label: 'Easy',
              },
            ]}
            className="difficulty-level"
          />
        </Form.Item>
        <Form.Item
          label='Play Time (min)'
          name='gamePlayTime'
          rules={[
            { required: true, message: "Play Time is Required." },
            {
              pattern: /^[0-9]*$/,
              message: 'Please enter numbers only'
            }
          ]}
        >
          <Input placeholder='Play Time' />
        </Form.Item>
        <Form.Item
          label='Game Setup Time (min)'
          name='gameResetTime'
          rules={[
            { required: true, message: "Game Setup Time is Required." },
            {
              pattern: /^[0-9]*$/,
              message: 'Please enter numbers only'
            }
          ]}
        >
          <Input placeholder='Game Reset Time' />
        </Form.Item>
        <Form.Item
          style={{ marginBottom: "24px" }}
          label='Game Image'
          name='gameImage'
          rules={[
            { required: true, message: "Please Upload Game Image!" }
          ]}
          className="upload-input"
        >
          <div>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                  setImageUrl(e.target.result);
                  setImage(file);
                  setCurrentImageType('game');
                  setShowCropper(true);
                };
                reader.readAsDataURL(file);
                return false;
              }}
            >
               <Button className="!text-black hover:!text-black focus:!text-black hover:!border-[#d9d9d9] focus:!border-[#d9d9d9] !w-full !h-11 flex items-center justify-center" icon={<div className="m-1"><UploadOutlined /></div>}>Click to Upload Game Image</Button>
            </Upload>
            {imageUrl && (
              <div className="mt-2">
                <img src={imageUrl} alt="Uploaded Image" style={{ maxHeight: "360px" }} className="mt-2" />
                {/* <a
                  onClick={() => {
                    setCurrentImageType('game');
                    setShowCropper(true);
                  }}
                  className="text-[#1890ff] cursor-pointer text-sm flex items-center gap-1 pt-2"
                >
                  <svg height="12" viewBox="0 0 48 48" width="12" xmlns="http://www.w3.org/2000/svg" fill="#1890ff"><path d="M0 0h48v48h-48z" fill="none" /><path d="M34 30h4v-16c0-2.21-1.79-4-4-4h-16v4h16v16zm-20 4v-32h-4v8h-8v4h8v20c0 2.21 1.79 4 4 4h20v8h4v-8h8v-4h-32z" /></svg> Crop Image
                </a> */}
              </div>
            )}
            <p className="text-danger mt-1 text-xs">* Upload image by 600 x 480 pixels</p>
          </div>
        </Form.Item>

        <Form.Item
          style={{ marginBottom: "24px" }}
          label='Game Slider Image'
          name='gameSliderImage'
          rules={[{ required: true, message: "Please Upload Game Slider Image!" }]}
          className="upload-input"
        >
          <div>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                  setSliderImageUrl(e.target.result);
                  setSliderImage(file);
                  setCurrentImageType('slider');
                  setShowCropper(true);
                };
                reader.readAsDataURL(file);
                return false;
              }}
            >
              <Button className="!text-black hover:!text-black focus:!text-black hover:!border-[#d9d9d9] focus:!border-[#d9d9d9] !w-full !h-11 flex items-center justify-center" icon={<div className="m-1"><UploadOutlined /></div>}>Click to Upload Slider Image</Button>
            </Upload>
            {sliderImageUrl && (
              <div className="mt-2">
                <img src={sliderImageUrl} alt="Uploaded Image" style={{ maxHeight: "360px" }} className="mt-2" />
                {/* <a
                  onClick={() => {
                    setCurrentImageType('slider');
                    setShowCropper(true);
                  }}
                  className="text-[#1890ff] cursor-pointer text-sm flex items-center gap-1 pt-2"
                >
                  <svg height="12" viewBox="0 0 48 48" width="12" xmlns="http://www.w3.org/2000/svg" fill="#1890ff"><path d="M0 0h48v48h-48z" fill="none" /><path d="M34 30h4v-16c0-2.21-1.79-4-4-4h-16v4h16v16zm-20 4v-32h-4v8h-8v4h8v20c0 2.21 1.79 4 4 4h20v8h4v-8h8v-4h-32z" /></svg> Crop Image
                </a> */}
              </div>
            )}
            <p className="text-danger mt-1 text-xs">* Upload image by 1920 x 640 pixels</p>
          </div>
        </Form.Item>

        {showCropper && (currentImageType === 'game' ? imageUrl : sliderImageUrl) && (
          <ImageCropper
            imageUrl={currentImageType === 'game' ? imageUrl : sliderImageUrl}
            crop={crop}
            zoom={zoom}
            aspect={currentImageType === 'game' ? 600/480 : 3/1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onClose={() => {
              setShowCropper(false);
              if (edit?.id) {
                // If editing, restore the original image
                if (currentImageType === 'game') {
                  setImageUrl(originalImageUrl);
                } else {
                  setSliderImageUrl(originalSliderImageUrl);
                }
              } else {
                // If creating new, clear the image
                if (currentImageType === 'game') {
                  setImageUrl('');
                  setImage(null);
                  form.setFieldsValue({ gameImage: undefined });
                } else {
                  setSliderImageUrl('');
                  setSliderImage(null);
                  form.setFieldsValue({ gameSliderImage: undefined });
                }
              }
            }}
            onCrop={cropImage}
          />
        )}

        <Form.Item label=''>
          <div className='flex items-center justify-end gap-2'>
            <Button
              size={"large"}
              htmlType='submit'
              type='danger'
              onClick={onCancel}
              className="btn-tertiary"
            >
              Cancel
            </Button>
            <Button size={"large"}
              htmlType='submit'
              loading={isSubmitting}
              className="btn-primary hover:border-[#E5383B]"
            >
              {edit?.id ? "Update" : "Create"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
}
